/* eslint-disable no-console -- diagnostic logs for the i18n usage reporter */

/**
 * Background reporter for the i18n key-usage tracker (`i18nDebug.ts`).
 *
 * Two persistent client-side gates share a single 15-min interval so the
 * server stays stateless:
 *   - POST debounce: a misbehaving client cannot flood writes faster than
 *     its own debounce, and a page reload reads the persisted timestamp so
 *     we don't double-report.
 *   - GET-disabled debounce: once the server answers `trackingEnabled:
 *     false`, we don't re-poll for 15 min — even across reloads or new
 *     tabs — so logged-in non-admin users don't burn one guaranteed-`false`
 *     round-trip per minute.
 *
 * The server is authoritative for whether tracking is on at all — the GET
 * flips `__i18nUsage.enable/disable` so a kill switch can stop the fleet
 * without a deploy. Cost of the 15-min GET gate: a flag flip from off→on
 * takes up to 15 min to propagate to clients already in the disabled window.
 */

import { contractClient } from 'src/services/orpc/contract'
import { safeUUID } from 'src/utils/genId'
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from 'src/utils/storage'
import { getToken } from 'src/utils/token'
import { __i18nUsage } from './i18nDebug'

const THROTTLE_INTERVAL_MS = 15 * 60 * 1000
const TICK_INTERVAL_MS = 60 * 1000
// Same window as the persistent throttle: in-session timer cap and the
// "ignore stale `false` answers" gate are conceptually one knob.
const TICK_INTERVAL_DISABLED_MAX_MS = THROTTLE_INTERVAL_MS
const IDLE_TIMEOUT_MS = 5_000
const STORAGE_KEY_LAST_REPORT = 'i18n:usage:last-report'
const STORAGE_KEY_LAST_DISABLED_GET = 'i18n:usage:last-disabled-get'

interface UsageEntry {
  ns: string
  lng: string
  key: string
  count: number
}

let scheduled = false
let batchStartTime = 0
let consecutiveDisabledTicks = 0

function readLastReportTime(): number | undefined {
  const raw = getLocalStorage(STORAGE_KEY_LAST_REPORT)
  if (!raw) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

function readLastDisabledGetTime(): number | undefined {
  const raw = getLocalStorage(STORAGE_KEY_LAST_DISABLED_GET)
  if (!raw) return undefined
  const n = Number(raw)
  return Number.isFinite(n) ? n : undefined
}

function getBuildTime(): number {
  // Reads `window.__build_timestamp` (set by `root.tsx` from
  // `VITE_BUILD_TIMESTAMP`). Same source as `runtimeConfig.buildTimestamp`,
  // but avoids importing `src/utils/config` which drags in unrelated
  // codegen-dependent modules through its own imports.
  return Number(window.__build_timestamp) || 0
}

function isReportable(e: UsageEntry): boolean {
  return e.count > 0 && !!e.ns && !!e.lng && !!e.key && e.lng !== 'unknown'
}

async function syncTrackingEnabled(): Promise<boolean> {
  const now = Date.now()
  const lastDisabled = readLastDisabledGetTime()
  if (lastDisabled !== undefined && now - lastDisabled < THROTTLE_INTERVAL_MS) {
    if (__i18nUsage.enabled()) __i18nUsage.disable()
    return false
  }

  const res = await contractClient.i18n.getTrackUsage({ key: safeUUID() })
  const serverEnabled = !!res.trackingEnabled
  const localEnabled = __i18nUsage.enabled()
  if (serverEnabled && !localEnabled) {
    __i18nUsage.enable()
    console.info('[i18nUsageReporter] tracking enabled')
  } else if (!serverEnabled && localEnabled) {
    __i18nUsage.disable()
    console.info('[i18nUsageReporter] tracking disabled')
  }

  if (serverEnabled) {
    removeLocalStorage(STORAGE_KEY_LAST_DISABLED_GET)
  } else {
    // Anchor on the moment we got the answer, not the moment we issued the
    // request — a slow round-trip would otherwise let us re-poll a few
    // seconds before the 15-min mark.
    setLocalStorage(STORAGE_KEY_LAST_DISABLED_GET, String(Date.now()))
  }
  return serverEnabled
}

async function reportUsageIfDue(): Promise<void> {
  const lastReport = readLastReportTime()
  const now = Date.now()
  if (lastReport !== undefined && now - lastReport < THROTTLE_INTERVAL_MS)
    return

  const usage = __i18nUsage
    .exportJson()
    .entries.filter(isReportable)
    .map(e => ({
      ns: e.ns,
      lang: e.lng,
      key: e.key,
      usageCount: e.count,
    }))

  if (usage.length === 0) {
    // Skip the localStorage write — keeping the timestamp at "last successful
    // POST" means an empty buffer doesn't reset the debounce window.
    batchStartTime = now
    return
  }

  await contractClient.i18n.postTrackUsage({
    buildInfo: {
      startTrackingTime: batchStartTime,
      buildTime: getBuildTime(),
    },
    usage,
  })

  __i18nUsage.reset()
  setLocalStorage(STORAGE_KEY_LAST_REPORT, String(now))
  batchStartTime = now
  console.info(
    `[i18nUsageReporter] reported ${usage.length} entries at ${new Date(now).toISOString()}`,
  )
}

/**
 * Cadence stretches when tracking has been disabled for several consecutive
 * ticks — caps at 15 min so a flag flip recovers within one window.
 */
function nextTickDelay(enabled: boolean): number {
  if (enabled) {
    consecutiveDisabledTicks = 0
    return TICK_INTERVAL_MS
  }
  consecutiveDisabledTicks++
  return Math.min(
    TICK_INTERVAL_MS * Math.min(consecutiveDisabledTicks, 15),
    TICK_INTERVAL_DISABLED_MAX_MS,
  )
}

async function tick(): Promise<void> {
  let enabled = false
  try {
    // Skip the GET entirely for logged-out users — tracking is admin-only
    // server-side, so anonymous traffic would just burn one round-trip per
    // tab per minute for a guaranteed `false` answer.
    if (!getToken()) return
    enabled = await syncTrackingEnabled()
    if (enabled) await reportUsageIfDue()
  } catch (err) {
    console.warn('[i18nUsageReporter] tick failed', err)
  } finally {
    scheduleAfter(nextTickDelay(enabled))
  }
}

function scheduleAfter(delayMs: number): void {
  setTimeout(() => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => void tick(), { timeout: IDLE_TIMEOUT_MS })
    } else {
      setTimeout(() => void tick(), 0)
    }
  }, delayMs)
}

/**
 * Idempotent — safe to call from the prelude block on every module load.
 * No-op outside the browser (SSR / Node imports) so it never schedules
 * background timers or network calls.
 */
export function startI18nUsageReporter(): void {
  if (typeof window === 'undefined') return
  if (scheduled) return
  scheduled = true
  batchStartTime = Date.now()
  scheduleAfter(0)
}
